/**
 * Development DateTime Override Service
 *
 * Provides a centralized way to override the system date/time during development.
 * In production, always returns the real current date/time.
 */

const DEV_DATETIME_STORAGE_KEY = 'godmode_dev_datetime_override';

interface DevDateTimeState {
  enabled: boolean;
  overrideTimestamp: number | null;
}

class DevDateTimeService {
  private state: DevDateTimeState = {
    enabled: false,
    overrideTimestamp: null,
  };

  private listeners: Set<() => void> = new Set();

  constructor() {
    this.loadState();
  }

  private loadState(): void {
    if (!import.meta.env.DEV) return;

    try {
      const stored = localStorage.getItem(DEV_DATETIME_STORAGE_KEY);
      if (stored) {
        this.state = JSON.parse(stored);
      }
    } catch (error) {
      console.error('Failed to load dev datetime state:', error);
    }
  }

  private saveState(): void {
    if (!import.meta.env.DEV) return;

    try {
      localStorage.setItem(DEV_DATETIME_STORAGE_KEY, JSON.stringify(this.state));
    } catch (error) {
      console.error('Failed to save dev datetime state:', error);
    }
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => listener());
  }

  /**
   * Subscribe to datetime override changes
   */
  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Get the current date - uses override if enabled in dev mode, otherwise real date
   */
  now(): Date {
    if (import.meta.env.DEV && this.state.enabled && this.state.overrideTimestamp !== null) {
      return new Date(this.state.overrideTimestamp);
    }
    return new Date();
  }

  /**
   * Get current timestamp - uses override if enabled in dev mode
   */
  nowTimestamp(): number {
    if (import.meta.env.DEV && this.state.enabled && this.state.overrideTimestamp !== null) {
      return this.state.overrideTimestamp;
    }
    return Date.now();
  }

  /**
   * Check if override is enabled (always false in production)
   */
  isOverrideEnabled(): boolean {
    return import.meta.env.DEV && this.state.enabled;
  }

  /**
   * Get the override date if set (null if not set or in production)
   */
  getOverrideDate(): Date | null {
    if (!import.meta.env.DEV || !this.state.enabled || this.state.overrideTimestamp === null) {
      return null;
    }
    return new Date(this.state.overrideTimestamp);
  }

  /**
   * Enable datetime override with specific date/time
   */
  setOverride(date: Date): void {
    if (!import.meta.env.DEV) {
      console.warn('DateTime override is only available in development mode');
      return;
    }

    this.state = {
      enabled: true,
      overrideTimestamp: date.getTime(),
    };
    this.saveState();
    this.notifyListeners();
    console.log('Dev DateTime Override enabled:', date.toISOString());
  }

  /**
   * Disable datetime override and return to real time
   */
  clearOverride(): void {
    if (!import.meta.env.DEV) return;

    this.state = {
      enabled: false,
      overrideTimestamp: null,
    };
    this.saveState();
    this.notifyListeners();
    console.log('Dev DateTime Override cleared, using real time');
  }

  /**
   * Check if dev mode is available
   */
  isDevMode(): boolean {
    return import.meta.env.DEV;
  }
}

export const devDateTimeService = new DevDateTimeService();

// Expose in development for debugging
if (import.meta.env.DEV && typeof window !== 'undefined') {
  (window as unknown as { devDateTime: DevDateTimeService }).devDateTime = devDateTimeService;
  console.log('Dev DateTime utilities available: window.devDateTime');
}
